import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { View } from 'react-native'

import ButtonIcon from '@nxt-insurance/ui-kit/components/ButtonIcon'
import Tag from '@nxt-insurance/ui-kit/components/Tag'

import type { NavbarItem, NavbarMenuLinkItem } from '@/cms/pages'
import { Icon } from '@/components/Icon'
import {
  navigationMenu,
  navigationMenuLink,
  navigationMenuTrigger,
} from '@/components/sections/Header/NavigationMenu/styles'
import { EVENTS, LINKS_IDS } from '@/constants/analytics'
import { useAnalytics } from '@/context/AnalyticsContext'
import { useUrlFromWebUrlReference } from '@/hooks/useUrlFromWebUrlReference'
import { reportErrorMessage } from '@/services/errorReporting'
import * as RadixNavigationMenu from '@radix-ui/react-navigation-menu'

const MultiColumnSection = dynamic(async () =>
  import('@/components/renderers/MultiColumnSection').then((mod) => mod.MultiColumnSection)
)

const NavigationLinkItem = (props: { item: NavbarMenuLinkItem }) => {
  const url = useUrlFromWebUrlReference(props.item.target ?? undefined)
  const { track } = useAnalytics()
  const { t } = useTranslation()

  return (
    <RadixNavigationMenu.Item>
      <RadixNavigationMenu.Link asChild>
        <Link
          className={navigationMenuLink()}
          href={url}
          onClick={() => {
            track(EVENTS.LinkClicked, {
              linkId: LINKS_IDS.HEADER_NAVIGATION_LOGIN,
              href: url,
              children: props.item.title,
            })
          }}
          aria-label={props.item.title}
        >
          {props.item.isNew && <Tag label={t('tag.new')} />}
          {props.item.title}
        </Link>
      </RadixNavigationMenu.Link>
    </RadixNavigationMenu.Item>
  )
}

const NavigationItem = (props: { item: NavbarItem; closeMenu: () => void }) => {
  const { t } = useTranslation()
  const { content } = navigationMenu()
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  if (props.item._type === 'website.richContent.navbarMenuItem') {
    if (!props.item.headerMenu) {
      return null
    }
    const backButtonTitle = t(
      'cookieConsentManager.preferencesDialog.table.cancelDialogButtons.backValue'
    )

    return (
      <RadixNavigationMenu.Item value={props.item._id}>
        <RadixNavigationMenu.Trigger ref={triggerRef} className={navigationMenuTrigger()}>
          {props.item.title}
          <View className="transition-all duration-500 ease-linear group-hover:rotate-180 group-data-[state=open]:rotate-180 lg:rotate-90 group-hover:lg:rotate-[270deg] group-data-[state=open]:lg:rotate-[270deg] ">
            <Icon size="small" icon={{ name: 'action.chevronRight_24' }} />
          </View>
        </RadixNavigationMenu.Trigger>
        <RadixNavigationMenu.Content className={content()}>
          <View className="order-0 border-1 -mx-6 my-0 flex h-14 w-full flex-row items-center justify-between border-ui-grey-ultra-light px-6 py-10 lg:order-1 lg:hidden lg:w-auto lg:border-0 lg:px-0 lg:py-0">
            <View className="grid grid-cols-2 gap-6 text-lg text-ui-dark">
              <ButtonIcon
                onPress={() => {
                  // This prevents the even from passing down to an element behind this button
                  setTimeout(() => {
                    triggerRef.current?.click()
                  }, 100)
                }}
                icon={
                  <View className="rotate-180">
                    <Icon
                      size="small"
                      icon={{ name: 'action.chevronLeft_24', alt: t('icon.close') }}
                    />
                  </View>
                }
                testID={backButtonTitle}
              />
              {t('header.nav.coverages')}
            </View>
            <View className="lg:hidden">
              <ButtonIcon
                onPress={props.closeMenu}
                icon={
                  <Icon size="small" icon={{ name: 'action.close_24', alt: t('icon.close') }} />
                }
              />
            </View>
          </View>
          <MultiColumnSection
            index={0}
            section={props.item.headerMenu}
            verticalPadding="medium"
            horizontalPadding="medium"
          />
        </RadixNavigationMenu.Content>
      </RadixNavigationMenu.Item>
    )
  } else if (props.item._type === 'website.richContent.navbarLinkItem') {
    return <NavigationLinkItem item={props.item} />
  }
  // @ts-expect-error _type should be of type never
  reportErrorMessage(`Assertion: Unknown item type: ${props.item._type}`)
  return null
}

export const NavigationMenu = (props: {
  items: NavbarItem[]
  closeMenu: () => void
  hasCampaign: boolean
}) => {
  const { root, list, viewport } = navigationMenu({ hasCampaign: props.hasCampaign })
  const [value, setValue] = useState('')
  const { asPath } = useRouter()
  const { closeMenu } = props
  useEffect(() => {
    closeMenu()
    setValue('')
    // we don't want to re-rerun this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asPath])
  return (
    <RadixNavigationMenu.Root className={root()} value={value} onValueChange={setValue}>
      <RadixNavigationMenu.List className={list()}>
        {props.items.map((item) => (
          <NavigationItem key={item._id} item={item} closeMenu={props.closeMenu} />
        ))}
        <RadixNavigationMenu.Indicator />
      </RadixNavigationMenu.List>
      <RadixNavigationMenu.Viewport className={viewport()} />
    </RadixNavigationMenu.Root>
  )
}
